import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../config";
import useUser from "../hooks/useUser";

const SECTIONS = [
  { category: "Science", topics: ["DNA", "Cryptography"] },
  {
    category: "Technology",
    topics: [
      "Scratch",
      "Computational Thinking",
      "Soundtrap",
      "UI Design",
      "Video Making and Video Editing",
    ],
  },
  { category: "Engineering", topics: ["TurtleStitch", "3D printing", "Electronics"] },
  { category: "Math", topics: ["Python Turtle"] },
];


const EXTERNAL_LINKS = {
  Scratch: "https://scratch.mit.edu/",
  TurtleStitch: "https://www.turtlestitch.org/",
  Soundtrap: "https://www.soundtrap.com/",
};

export default function Resources() {
  const navigate = useNavigate();
  const { user, authHeaders } = useUser();
  const isAdmin = user?.role === "admin";

  const [items, setItems] = useState([]);
  const [openKey, setOpenKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState("");

  const [form, setForm] = useState({
    category: "Technology",
    topic: "Scratch",
    customTopic: "",
    title: "",
    description: "",
    link: "",
  });

  async function loadResources() {
    if (!user) return;

    setLoading(true);
    setErr("");

    try {
      const res = await fetch(`${API_BASE_URL}/api/resources`, {
        headers: authHeaders(),
      });

      const text = await res.text();

      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch {
        throw new Error("Server returned an unexpected response. Please try again.");
      }

      if (!res.ok) {
        throw new Error(data?.message || "Failed to load resources.");
      }

      setItems(Array.isArray(data.resources) ? data.resources : []);
    } catch (e) {
      setErr(e.message || "Failed to load resources.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (user) loadResources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const grouped = useMemo(() => {
    const map = {};

   
    for (const sec of SECTIONS) {
      for (const topic of sec.topics) {
        const key = `${sec.category}__${topic}`;
        map[key] = [];
      }
    }

    
    for (const it of items) {
      const key = `${it.category}__${it.topic}`;
      if (!map[key]) map[key] = [];
      map[key].push(it);
    }

    return map;
  }, [items]);

  const sectionData = useMemo(() => {
    return SECTIONS.map((sec) => {
      const customTopicsInCategory = Array.from(
        new Set(
          items
            .filter(
              (item) =>
                item.category === sec.category && !sec.topics.includes(item.topic)
            )
            .map((item) => item.topic)
        )
      );

      return {
        ...sec,
        allTopics: [...sec.topics, ...customTopicsInCategory],
      };
    });
  }, [items]);

  function toggle(key) {
    setOpenKey((k) => (k === key ? "" : key));
  }

  function resetForm() {
    setForm({
      category: "Technology",
      topic: "Scratch",
      customTopic: "",
      title: "",
      description: "",
      link: "",
    });
    setIsEditing(false);
    setEditingId("");
  }

  function getFinalTopic() {
    return form.topic === "Other" ? form.customTopic.trim() : form.topic;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setErr("");

    if (!isAdmin) {
      setErr("Admin privileges required.");
      return;
    }

    const finalTopic = getFinalTopic();

    if (!finalTopic) {
      setErr("Please enter a topic name.");
      return;
    }

    const payload = {
      category: form.category,
      topic: finalTopic,
      title: form.title,
      description: form.description,
      link: form.link,
      isExternal: Boolean(form.link && EXTERNAL_LINKS[finalTopic]),
    };

    try {
      const url = isEditing
        ? `${API_BASE_URL}/api/resources/${editingId}`
        : `${API_BASE_URL}/api/resources`;

      const method = isEditing ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify(payload),
      });

      const text = await res.text();

      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch {
        throw new Error("Server returned an unexpected response. Please try again.");
      }

      if (!res.ok) {
        throw new Error(
          data?.message || `Failed to ${isEditing ? "update" : "add"} resource.`
        );
      }

      resetForm();
      await loadResources();
    } catch (e) {
      setErr(e.message || `Failed to ${isEditing ? "update" : "add"} resource.`);
    }
  }

  function startEdit(resource) {
    const predefinedTopics =
      SECTIONS.find((s) => s.category === resource.category)?.topics || [];

    const isCustomTopic = !predefinedTopics.includes(resource.topic);

    setForm({
      category: resource.category,
      topic: isCustomTopic ? "Other" : resource.topic,
      customTopic: isCustomTopic ? resource.topic : "",
      title: resource.title || "",
      description: resource.description || "",
      link: resource.link || "",
    });

    setIsEditing(true);
    setEditingId(resource._id);
    setErr("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function deleteResource(id) {
    setErr("");
    if (!isAdmin) return;

    try {
      const res = await fetch(`${API_BASE_URL}/api/resources/${id}`, {
        method: "DELETE",
        headers: authHeaders(),
      });

      const text = await res.text();

      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch {
        throw new Error("Server returned an unexpected response. Please try again.");
      }

      if (!res.ok) {
        throw new Error(data?.message || "Failed to delete resource.");
      }

      if (editingId === id) resetForm();
      await loadResources();
    } catch (e) {
      setErr(e.message || "Failed to delete resource.");
    }
  }

  if (!user) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-10">
        <div className="bg-white rounded-2xl border shadow-soft p-6">
          <h1 className="text-3xl font-bold text-ink">Learning Resources</h1>
          <p className="mt-2 text-ink/70">
            Please log in to access the learning resources.
          </p>

          <div className="mt-6 flex gap-3">
            <button
              onClick={() => navigate("/login")}
              className="rounded-xl bg-brand-600 text-white px-4 py-2 font-medium hover:bg-brand-700"
            >
              Log in
            </button>
            <button
              onClick={() => navigate("/register")}
              className="rounded-xl border border-brand-300 text-brand-700 px-4 py-2 font-medium hover:bg-brand-50"
            >
              Create an account
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentSection =
    SECTIONS.find((s) => s.category === form.category) || SECTIONS[0];

  return (
    <div className="max-w-6xl mx-auto px-4 py-10">
      <header className="bg-white rounded-2xl border shadow-soft p-6">
        <h1 className="text-3xl font-bold text-ink">Learning Resources</h1>
        <p className="mt-2 text-ink/70">
          Explore resources across Science, Technology, Engineering, and Maths.
          Some topics link to official external pages, while others will contain
          Maynooth University materials later.
        </p>
      </header>

      {err && (
        <div
          role="alert"
          className="mt-6 rounded-lg border border-red-300 bg-red-50 text-red-700 p-3"
        >
          {err}
        </div>
      )}

      {loading && (
        <div className="mt-6 text-sm text-ink/70">Loading resources…</div>
      )}

      {isAdmin && (
        <section className="mt-6 bg-white rounded-2xl border shadow-soft p-6">
          <h2 className="text-xl font-semibold text-ink">
            Admin: {isEditing ? "Edit Resource" : "Add a Resource"}
          </h2>
          <p className="text-sm text-ink/70 mt-1">
            This creates or updates a resource entry in MongoDB.
          </p>

          <form onSubmit={handleSubmit} className="mt-4 grid gap-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="grid gap-1">
                <label className="text-sm font-medium">Category</label>
                <select
                  className="rounded-xl border px-3 py-2"
                  value={form.category}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      category: e.target.value,
                      topic:
                        SECTIONS.find((s) => s.category === e.target.value)?.topics[0] ||
                        "Other",
                      customTopic: "",
                    }))
                  }
                >
                  {SECTIONS.map((s) => (
                    <option key={s.category} value={s.category}>
                      {s.category}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid gap-1">
                <label className="text-sm font-medium">Topic</label>
                <select
                  className="rounded-xl border px-3 py-2"
                  value={form.topic}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      topic: e.target.value,
                      customTopic: e.target.value === "Other" ? f.customTopic : "",
                    }))
                  }
                >
                  {currentSection.topics.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            {form.topic === "Other" && (
              <div className="grid gap-1">
                <label className="text-sm font-medium">New Topic Name *</label>
                <input
                  className="rounded-xl border px-3 py-2"
                  placeholder="e.g. Physics in Action"
                  required
                  value={form.customTopic}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, customTopic: e.target.value }))
                  }
                />
              </div>
            )}

            <div className="grid gap-1">
              <label className="text-sm font-medium">Title *</label>
              <input
                className="rounded-xl border px-3 py-2"
                required
                value={form.title}
                onChange={(e) =>
                  setForm((f) => ({ ...f, title: e.target.value }))
                }
              />
            </div>

            <div className="grid gap-1">
              <label className="text-sm font-medium">Description</label>
              <textarea
                className="rounded-xl border px-3 py-2"
                rows={3}
                value={form.description}
                onChange={(e) =>
                  setForm((f) => ({ ...f, description: e.target.value }))
                }
              />
            </div>

            <div className="grid gap-1">
              <label className="text-sm font-medium">Link (optional)</label>
              <input
                className="rounded-xl border px-3 py-2"
                placeholder="https://..."
                value={form.link}
                onChange={(e) =>
                  setForm((f) => ({ ...f, link: e.target.value }))
                }
              />
              <p className="text-xs text-ink/60">
                For Scratch, TurtleStitch, or Soundtrap, the official link can
                be used. For other internal materials, this can be left blank.
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <button className="rounded-xl bg-brand-600 text-white px-4 py-2 font-medium hover:bg-brand-700">
                {isEditing ? "Save Changes" : "Add Resource"}
              </button>

              {isEditing && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="rounded-xl border border-brand-300 text-brand-700 px-4 py-2 font-medium hover:bg-brand-50"
                >
                  Cancel Edit
                </button>
              )}
            </div>
          </form>
        </section>
      )}

      <section className="mt-8 grid gap-6">
        {sectionData.map((sec) => (
          <div
            key={sec.category}
            className="bg-white rounded-2xl border shadow-soft overflow-hidden"
          >
            <div className="px-6 py-5 border-b bg-brand-50">
              <h2 className="text-2xl font-bold text-ink">{sec.category}</h2>
            </div>

            <div className="p-6 grid gap-3">
              {sec.allTopics.map((topic) => {
                const key = `${sec.category}__${topic}`;
                const isOpen = openKey === key;
                const list = grouped[key] || [];
                const external = EXTERNAL_LINKS[topic];

                return (
                  <div key={key} className="rounded-2xl border">
                    <button
                      type="button"
                      onClick={() => toggle(key)}
                      className="w-full flex items-center justify-between px-4 py-4 text-left"
                    >
                      <div>
                        <p className="font-semibold text-ink">{topic}</p>
                        <p className="text-sm text-ink/60">
                          {external
                            ? "Official external page available."
                            : "MU resources will be added later."}
                        </p>
                      </div>
                      <span className="text-ink/60">{isOpen ? "−" : "+"}</span>
                    </button>

                    {isOpen && (
                      <div className="px-4 pb-4">
                        {list.length === 0 ? (
                          <p className="text-sm text-ink/70">
                            No resources added yet.
                          </p>
                        ) : (
                          <ul className="grid gap-2">
                            {list.map((r) => (
                              <li key={r._id} className="rounded-xl border p-3">
                                <p className="font-medium text-ink">{r.title}</p>

                                {r.description && (
                                  <p className="text-sm text-ink/70 mt-1">
                                    {r.description}
                                  </p>
                                )}

                                {r.link && (
                                  <a
                                    className="text-sm text-brand-700 underline mt-2 inline-block"
                                    href={r.link}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    Open link →
                                  </a>
                                )}

                                {isAdmin && (
                                  <div className="mt-3 flex gap-4">
                                    <button
                                      type="button"
                                      onClick={() => startEdit(r)}
                                      className="text-sm text-brand-700 underline"
                                    >
                                      Edit
                                    </button>

                                    <button
                                      type="button"
                                      onClick={() => deleteResource(r._id)}
                                      className="text-sm text-red-700 underline"
                                    >
                                      Delete
                                    </button>
                                  </div>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}

                        <div className="mt-3">
                          {external ? (
                            <a
                              className="text-sm font-medium text-brand-700 underline"
                              href={external}
                              target="_blank"
                              rel="noreferrer"
                            >
                              More info →
                            </a>
                          ) : (
                            <span className="text-sm text-ink/60">
                              More info → (MU page to be added later)
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </section>
    </div>
  );
}